import { useMemo, useState } from 'react';
import { api, describeError, useOp } from '../../api.ts';
import { Link } from '../../router.tsx';
import { announceChange } from '../../ui/changes.ts';
import { Empty, ErrorBox, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import './intake.css';

/**
 * Coverage (docs/coverage-and-evergreens.md): how many coupons each site has per store, where the gaps are (no codes,
 * missing evergreens, codes competitors list), and the two tools that close them: the evergreen search and the copy
 * refresh.
 */

type Kind = 'newsletter' | 'first_order' | 'app' | 'student' | 'key_workers' | 'loyalty' | 'referral' | 'free_delivery' | 'sale';

interface Store {
  site: { ref: string; name: string };
  merchant: { ref: string; name: string; slug: string };
  countryCode: string;
  popularity: number;
  live: number;
  codes: number;
  pending: number;
  expiringSoon: number;
  evergreens: Kind[];
  missingCore: Kind[];
  missed: { code: string; title: string; source: string; lastSeenAt: string }[];
  lastEvergreenSearchAt: string | null;
  hasLogo: boolean;
  gapScore: number;
  gaps: string[];
}

interface Site {
  ref: string;
  name: string;
  countryCode: string;
  stores: number;
  storesWithoutCoupons: number;
  storesWithoutCodes: number;
  storesMissingEvergreens: number;
  liveCoupons: number;
  liveCodes: number;
  evergreens: number;
  expiringSoon: number;
  pending: number;
  missedCodes: number;
  storesWithoutLogo: number;
}

interface Report {
  sites: Site[];
  stores: Store[];
  unknownStores: { countryCode: string; domain: string; name: string | null; codes: number; lastSeenAt: string }[] | null;
}

interface FinderResult {
  merchant: { ref: string; name: string; countryCode: string };
  found: number;
  skipped: { offer: string; reason: string }[];
  results: { status: string; message: string; coupon: { ref: string; title: string } | null; sites: { name: string; title: string | null; state: string; why: string[] }[] }[];
  costMicros: number;
  error: string | null;
}

interface RefreshResult {
  rewritten: number;
  items: { couponRef: string; site: string; merchant: string; before: { title: string; description: string }; after: { title: string; description: string; origin: string } | null; reason: string | null }[];
}

const KIND_LABEL: Record<Kind, string> = {
  newsletter: 'Newsletter',
  first_order: 'First order',
  app: 'App',
  student: 'Student',
  key_workers: 'Key workers',
  loyalty: 'Loyalty',
  referral: 'Referral',
  free_delivery: 'Free delivery',
  sale: 'Sale',
};

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
}

export function Coverage() {
  const toast = useToast();
  const [siteRef, setSiteRef] = useState('');
  const [onlyGaps, setOnlyGaps] = useState(true);
  const [query, setQuery] = useState('');
  const [reload, setReload] = useState(0);
  const op = useOp<Report>('admin.coverage.overview', { ...(siteRef ? { propertyRef: siteRef } : {}), onlyGaps }, reload);
  const [busy, setBusy] = useState<string | null>(null);
  const [found, setFound] = useState<FinderResult | null>(null);
  const [refresh, setRefresh] = useState<{ preview: boolean; result: RefreshResult } | null>(null);

  const stores = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (op.data?.stores ?? []).filter((s) => !q || s.merchant.name.toLowerCase().includes(q) || s.merchant.slug.includes(q));
  }, [op.data, query]);

  const findEvergreens = async (store: Store) => {
    setBusy(store.merchant.ref);
    try {
      const result = await api.op<FinderResult>('admin.coverage.findEvergreens', { merchantRef: store.merchant.ref });
      setFound(result);
      if (result.error) toast('error', result.error);
      else {
        const made = result.results.filter((r) => r.status === 'created').length;
        toast(made ? 'success' : 'info', made ? `${made} evergreen coupon(s) created for ${result.merchant.name}` : `Nothing new for ${result.merchant.name}`);
        announceChange();
        setReload((n) => n + 1);
      }
    } catch (error) {
      toast('error', describeError(error));
    }
    setBusy(null);
  };

  const fillGaps = async () => {
    setBusy('gaps');
    try {
      const result = await api.op<{ searched: { ref: string; name: string; created: number; error: string | null }[] }>('admin.coverage.fillGaps', { limit: 3 });
      const made = result.searched.reduce((sum, s) => sum + s.created, 0);
      toast(result.searched.length ? 'success' : 'info', result.searched.length ? `Searched ${result.searched.map((s) => s.name).join(', ')}: ${made} coupon(s) created` : 'No store left to search right now');
      announceChange();
      setReload((n) => n + 1);
    } catch (error) {
      toast('error', describeError(error));
    }
    setBusy(null);
  };

  const fetchLogos = async () => {
    setBusy('logos');
    try {
      const result = await api.op<{ results: { name: string; url: string | null; error: string | null }[]; remaining: number }>('admin.masterMerchants.fetchMissingLogos', { limit: 10 });
      const found = result.results.filter((r) => r.url).length;
      toast(result.results.length ? 'success' : 'info', result.results.length ? `${found} of ${result.results.length} logos found${result.remaining ? `; ${result.remaining} stores still without` : ''}` : 'Every store shown on a site has a logo (or was tried this week)');
      announceChange();
      setReload((n) => n + 1);
    } catch (error) {
      toast('error', describeError(error));
    }
    setBusy(null);
  };

  const runRefresh = async (dryRun: boolean) => {
    setBusy('refresh');
    try {
      const result = await api.op<RefreshResult>('admin.coverage.refreshCopy', { ...(siteRef ? { propertyRef: siteRef } : {}), dryRun, limit: 30 });
      setRefresh({ preview: dryRun, result });
      if (!dryRun) {
        toast('success', `${result.rewritten} coupon text(s) rewritten`);
        announceChange();
      }
    } catch (error) {
      toast('error', describeError(error));
    }
    setBusy(null);
  };

  const undo = async (couponRef: string, site: string) => {
    const siteRow = op.data?.sites.find((s) => s.name === site);
    if (!siteRow) return;
    try {
      await api.op('admin.coverage.undoRefresh', { couponRef, propertyRef: siteRow.ref });
      toast('success', 'Previous text restored');
      announceChange();
    } catch (error) {
      toast('error', describeError(error));
    }
  };

  return (
    <section className="page intake">
      <header className="page-head">
        <div>
          <h1>Coverage</h1>
          <p className="muted">
            Coupons per site and store, and where they are missing: stores without codes, missing evergreens (newsletter sign-up, first order), codes competitors
            list that we do not, and coupons running out. Biggest gaps of the most popular stores first.
          </p>
        </div>
        <div className="form-actions">
          <button type="button" className="btn" disabled={!!busy} onClick={() => void fillGaps()} title="Searches the stores' own websites for evergreen savings, never-searched stores first">
            {busy === 'gaps' ? 'Searching…' : 'Find evergreens for 3 stores'}
          </button>
          <button type="button" className="btn" disabled={!!busy} onClick={() => void fetchLogos()} title="Fetches logos from the stores' own websites for stores without one, most popular first">
            {busy === 'logos' ? 'Fetching…' : 'Fetch 10 missing logos'}
          </button>
          <button type="button" className="btn" disabled={!!busy} onClick={() => void runRefresh(true)} title="Shows which coupon texts are due for a rewrite and the new text">
            {busy === 'refresh' ? 'Checking…' : 'Refresh copy…'}
          </button>
        </div>
      </header>

      {op.loading && !op.data ? <Loading /> : null}
      {op.error ? <ErrorBox error={op.error} onRetry={op.reload} /> : null}

      {op.data ? (
        <>
          <div className="table-wrap">
            <table className="table" aria-label="Sites">
              <thead>
                <tr>
                  <th scope="col">Site</th>
                  <th scope="col">Stores</th>
                  <th scope="col">Live coupons</th>
                  <th scope="col">Codes</th>
                  <th scope="col">Without coupons</th>
                  <th scope="col">Without codes</th>
                  <th scope="col">Missing evergreens</th>
                  <th scope="col">Expiring (7 days)</th>
                  <th scope="col">Waiting</th>
                  <th scope="col">Without logo</th>
                  {op.data.unknownStores ? <th scope="col">Codes competitors have</th> : null}
                </tr>
              </thead>
              <tbody>
                {op.data.sites.map((site) => (
                  <tr key={site.ref} className={siteRef === site.ref ? 'row-selected' : undefined}>
                    <td>
                      <button type="button" className="link-button" onClick={() => setSiteRef(siteRef === site.ref ? '' : site.ref)}>
                        {site.name}
                      </button>{' '}
                      <span className="muted small">{site.countryCode}</span>
                    </td>
                    <td>{site.stores}</td>
                    <td>{site.liveCoupons}</td>
                    <td>{site.liveCodes}</td>
                    <td className={site.storesWithoutCoupons ? 'text-warn' : ''}>{site.storesWithoutCoupons}</td>
                    <td>{site.storesWithoutCodes}</td>
                    <td className={site.storesMissingEvergreens ? 'text-warn' : ''}>{site.storesMissingEvergreens}</td>
                    <td>{site.expiringSoon}</td>
                    <td>{site.pending}</td>
                    <td className={site.storesWithoutLogo ? 'text-warn' : ''}>{site.storesWithoutLogo}</td>
                    {op.data!.unknownStores ? <td>{site.missedCodes}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="coverage-filters">
            <label className="inline-field">
              Store
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search stores" />
            </label>
            <label className="inline-field">
              <input type="checkbox" checked={onlyGaps} onChange={(e) => setOnlyGaps(e.target.checked)} /> Only stores with gaps
            </label>
            {siteRef ? (
              <button type="button" className="btn btn-small btn-ghost" onClick={() => setSiteRef('')}>
                All sites
              </button>
            ) : null}
          </div>

          {stores.length === 0 ? (
            <Empty>No gaps here.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table" aria-label="Stores">
                <thead>
                  <tr>
                    <th scope="col">Store</th>
                    <th scope="col">Site</th>
                    <th scope="col">Live</th>
                    <th scope="col">Codes</th>
                    <th scope="col">Evergreens</th>
                    <th scope="col">Gaps</th>
                    <th scope="col">Searched</th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {stores.map((store) => (
                    <tr key={`${store.site.ref}:${store.merchant.ref}`}>
                      <td>
                        <Link to={`/merchants/${store.merchant.ref}`}>{store.merchant.name}</Link>
                      </td>
                      <td className="small">{store.site.name}</td>
                      <td>{store.live}</td>
                      <td>{store.codes}</td>
                      <td className="small">
                        {store.evergreens.map((kind) => (
                          <span key={kind} className="badge badge-ok">
                            {KIND_LABEL[kind]}
                          </span>
                        ))}{' '}
                        {store.missingCore.map((kind) => (
                          <span key={kind} className="badge badge-warn" title="Missing: most shops have this">
                            no {KIND_LABEL[kind].toLowerCase()}
                          </span>
                        ))}
                      </td>
                      <td className="small">
                        {store.gaps.join(' · ')}
                        {store.missed.length ? (
                          <details>
                            <summary>Codes competitors list</summary>
                            <ul>
                              {store.missed.map((m) => (
                                <li key={m.code}>
                                  <code>{m.code}</code> {m.title} <span className="muted">({m.source}, {ago(m.lastSeenAt)})</span>
                                </li>
                              ))}
                            </ul>
                          </details>
                        ) : null}
                      </td>
                      <td className="small muted">{ago(store.lastEvergreenSearchAt)}</td>
                      <td>
                        <button type="button" className="btn btn-small" disabled={!!busy} onClick={() => void findEvergreens(store)}>
                          {busy === store.merchant.ref ? 'Searching…' : 'Find evergreens'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {op.data.unknownStores && op.data.unknownStores.length ? (
            <>
              <h2 className="section-title">Stores competitors list that we do not carry</h2>
              <table className="table" aria-label="Stores we do not carry">
                <thead>
                  <tr>
                    <th scope="col">Domain</th>
                    <th scope="col">Country</th>
                    <th scope="col">Codes seen</th>
                    <th scope="col">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {op.data.unknownStores.map((s) => (
                    <tr key={`${s.countryCode}:${s.domain}`}>
                      <td>
                        {s.name ? `${s.name} · ` : ''}
                        {s.domain}
                      </td>
                      <td>{s.countryCode}</td>
                      <td>{s.codes}</td>
                      <td className="small muted">{ago(s.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </>
      ) : null}

      {found ? (
        <div className="intake-results" aria-live="polite">
          <h2 className="section-title">
            Evergreen search: {found.merchant.name} · {found.merchant.countryCode}{' '}
            <button type="button" className="btn btn-small btn-ghost" onClick={() => setFound(null)}>
              Close
            </button>
          </h2>
          {found.error ? <p className="text-warn">{found.error}</p> : <p className="muted small">Found {found.found} on the shop’s own site · cost about ${(found.costMicros / 1e6).toFixed(3)}</p>}
          {found.results.map((result, index) => (
            <article key={index} className="intake-card">
              <header>
                <span className={`badge ${result.status === 'created' ? 'badge-ok' : ''}`}>{result.status}</span>
                {result.coupon ? <Link to={`/coupons?coupon=${result.coupon.ref}`}>{result.coupon.ref}</Link> : null}
              </header>
              <p>{result.message}</p>
              <ul className="small">
                {result.sites.map((site) => (
                  <li key={site.name}>
                    <strong>{site.name}</strong>: {site.title ?? '—'} <span className={`badge ${site.state === 'approved' ? 'badge-ok' : ''}`}>{site.state === 'approved' ? 'live' : site.state.replace('_', ' ')}</span>
                    {site.why.length ? <span className="text-warn"> {site.why.join(' · ')}</span> : null}
                  </li>
                ))}
              </ul>
            </article>
          ))}
          {found.skipped.length ? (
            <details>
              <summary>Left out ({found.skipped.length})</summary>
              <ul className="small">
                {found.skipped.map((s) => (
                  <li key={s.offer}>
                    {s.offer}: <span className="muted">{s.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      {refresh ? (
        <div className="intake-results" aria-live="polite">
          <h2 className="section-title">{refresh.preview ? `Copy refresh: ${refresh.result.items.filter((i) => i.after).length} due (nothing changed yet)` : `Copy refresh: ${refresh.result.rewritten} rewritten`}</h2>
          {refresh.result.items.length === 0 ? <Empty>No coupon text is due: generated texts are rewritten after six weeks.</Empty> : null}
          {refresh.result.items.length ? (
            <table className="table" aria-label="Copy refresh">
              <thead>
                <tr>
                  <th scope="col">Store · site</th>
                  <th scope="col">Now</th>
                  <th scope="col">New</th>
                  {!refresh.preview ? <th scope="col" /> : null}
                </tr>
              </thead>
              <tbody>
                {refresh.result.items.map((item) => (
                  <tr key={`${item.couponRef}:${item.site}`}>
                    <td className="small">
                      {item.merchant} · {item.site}
                    </td>
                    <td className="small">
                      <strong>{item.before.title}</strong>
                      <div className="muted">{item.before.description}</div>
                    </td>
                    <td className="small">
                      {item.after ? (
                        <>
                          <strong>{item.after.title}</strong>
                          <div className="muted">{item.after.description}</div>
                        </>
                      ) : (
                        <span className="text-warn">{item.reason}</span>
                      )}
                    </td>
                    {!refresh.preview ? (
                      <td>
                        {item.after ? (
                          <button type="button" className="btn btn-small btn-ghost" onClick={() => void undo(item.couponRef, item.site)}>
                            Undo
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <div className="form-actions">
            {refresh.preview && refresh.result.items.some((i) => i.after) ? (
              <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => void runRefresh(false)}>
                {busy === 'refresh' ? 'Rewriting…' : 'Rewrite these'}
              </button>
            ) : null}
            <button type="button" className="btn btn-ghost" onClick={() => setRefresh(null)}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
